import { altW, fromPredicate, getOrElseW, map } from 'fp-ts/lib/Option';
import { map as mapA } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';

export type PaginationOptions = {
  count: number;
  boundary?: number;
  siblings?: number;
  current?: number;
};

export type PaginationItem = {
  type: 'next' | 'previous' | 'start-ellipsis' | 'end-ellipsis' | number;
  active: boolean;
};

const range = (start: number) => (end: number) =>
  pipe(end - start + 1, (length) =>
    Array.from({ length }, (_, i) => start + i)
  );
export function usePagination({
  count,
  boundary = 1,
  siblings = 1,
  current = 1,
}: PaginationOptions) {
  const startPages = useMemo(
    () => pipe(Math.min(boundary, count), range(1)),
    [boundary, count]
  );
  const endPages = useMemo(
    () => pipe(count, range(Math.max(count - boundary + 1, boundary + 1))),
    [boundary, count]
  );

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      current - siblings,
      // Lower boundary when page is high
      count - boundary - siblings * 2 - 1
    ),
    // Greater than startPages
    boundary + 2
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      current + siblings,
      // Upper boundary when page is low
      boundary + siblings * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  );

  const startEllipsisOrCurrent = useMemo(
    () =>
      pipe(
        siblingsStart,
        fromPredicate((x) => x > boundary + 2),
        map(() => ['start-ellipsis']),
        altW(() =>
          pipe(
            boundary,
            fromPredicate((x) => x + 1 < count - x),
            map((x) => [x + 1])
          )
        ),
        getOrElseW(() => [])
      ),
    [boundary, siblingsStart, count]
  );

  const endEllipsisOrCurrent = useMemo(
    () =>
      pipe(
        siblingsEnd,
        fromPredicate((x) => x < count - boundary - 1),
        map(() => ['end-ellipsis']),
        altW(() =>
          pipe(
            boundary,
            fromPredicate((x) => count - x > x),
            map((x) => [count - x])
          )
        ),
        getOrElseW(() => [])
      ),
    [boundary, siblingsEnd, count]
  );

  const siblingPages = useMemo(
    () => pipe(siblingsEnd, range(siblingsStart)),
    [siblingsStart, siblingsEnd]
  );

  const items = useMemo(
    () => [
      'previous',
      ...startPages,
      ...startEllipsisOrCurrent,
      ...siblingPages,
      ...endEllipsisOrCurrent,
      ...endPages,
      'next',
    ],
    [
      startPages,
      startEllipsisOrCurrent,
      siblingPages,
      endEllipsisOrCurrent,
      endPages,
    ]
  );

  return useMemo(
    () =>
      pipe(
        items,
        mapA(
          (item) =>
            ({
              type: item,
              active: parseInt(current.toString()) === item,
            } as PaginationItem)
        )
      ),
    [items, current]
  );
}
